import React, { useState, useRef } from 'react';
import {
  IconButton,
  Popover,
  Box,
  Typography,
  Paper,
  Divider,
  Tooltip
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { ThresholdSlider } from './ThresholdSlider';
import { Clear } from '@mui/icons-material';

interface SettingsDropdownProps {
  threshold: number;
  onThresholdChange: (value: number) => void;
  disabled?: boolean;
}

export const SettingsDropdown: React.FC<SettingsDropdownProps> = ({
  threshold,
  onThresholdChange,
  disabled = false
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'settings-popover' : undefined;

  return (
    <>
      <Tooltip title="Settings">
        <IconButton
          ref={buttonRef}
          aria-describedby={id}
          onClick={handleClick}
          disabled={disabled}
          sx={{
            '&:hover': {
              bgcolor: 'action.hover',
            },
            '&.Mui-disabled': {
              opacity: 0.5,
            }
          }}
        >
          <SettingsIcon />
        </IconButton>
      </Tooltip>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            ml: -1,
            minWidth: 400,
            maxWidth: 450,
          }
        }}
      >
        <Paper sx={{ p: 0 }}>
          {/* Header */}
          <Box sx={{ p: 2, pb: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" gutterBottom>
                Settings
              </Typography>
              <IconButton
                size="small"
                onClick={handleClose}
                disabled={disabled}
                title="Close"
              >
                <Clear />
              </IconButton>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Adjust recognition parameters
            </Typography>
          </Box>

          <Divider />

          {/* Threshold */}
          <Box sx={{ p: 2 }}>
            <ThresholdSlider
              value={threshold}
              min={0}
              max={100}
              step={1}
              label="Similarity Threshold"
              unit="%"
              onChange={onThresholdChange}
              disabled={disabled}
            />
          </Box>
        </Paper>
      </Popover>
    </>
  );
}; 